import axios from "axios";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config";
import ServerTablePagination from "../../components/shared/ServerTablePagination";
import { useAuth } from "../../components/shared/AuthContext";
import { Button, PageHeader, Badge, TableSkeleton, ConfirmModal, Modal, FilterIcon, PlusIcon, EmptyState, SearchInput, PageLayout, Select } from "../../components/ui";

/* ── Alert Modal (themed) ────────────────────────────── */
const AlertModal = ({ isOpen, onClose, title, message }) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    title={title}
    size="sm"
    footer={
      <Button variant="primary" size="sm" onClick={onClose}>
        OK
      </Button>
    }
  >
    <p className="text-sm text-ink-500 leading-relaxed">{message}</p>
  </Modal>
);

/* ── Customers Page ──────────────────────────────────── */
const Customers = () => {
  const { permissions, roleName, data, jwt } = useAuth();
  const navigate = useNavigate();

  const [customerData, setCustomerData] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0 });
  const [loading, setLoading] = useState(false);

  const [showDelModal, setShowDelModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showUnverifiedOnly, setShowUnverifiedOnly] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [selectedStatus, setSelectedStatus] = useState("");
  const [fieldOfficers, setFieldOfficers] = useState([]);
  const [selectedOfficer, setSelectedOfficer] = useState("");
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");

  // Fetch field officers on mount
  useEffect(() => {
    const fetchOfficers = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/getModelData/staff_details`, {
          params: { limit: 500 }
        });
        const staff = response.data.data || [];
        const officers = staff.filter(s => Number(s.roleId) === 3 || s.designation?.toLowerCase().includes("field officer"));
        setFieldOfficers(officers.length > 0 ? officers : staff);
      } catch (error) {
        console.error("Error fetching field officers:", error);
      }
    };
    fetchOfficers();
  }, []);

  // Branches, for the superadmin-only Branch filter.
  //
  // Only fetched for a superadmin: every other role is pinned to their own
  // branch and never sees the dropdown, so the request would be wasted.
  useEffect(() => {
    if (roleName !== "superadmin") return;
    const fetchBranches = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/getModelData/branch_details`, {
          params: { fields: "id,branchName", limit: 500 },
        });
        setBranches(response.data.data || []);
      } catch (error) {
        console.error("Error fetching branches:", error);
      }
    };
    fetchBranches();
  }, [roleName]);

  /* ── Permissions ──────────────────────────────────── */
  const hasAddPermission = permissions.some((p) => p.page === "customers" && p.add);
  const hasEditPermission = permissions.some((p) => p.page === "customers" && p.edit);
  const hasDelPermission = permissions.some((p) => p.page === "customers" && p.del);
  const isAdminRole = roleName === "admin" || roleName === "superadmin" || roleName === "super admin";

  /* ── Debounce search ──────────────────────────────── */
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  /* ── Fetch ────────────────────────────────────────── */
  const fetchData = useCallback(
    async (page = 1, limit = 15, unverified = showUnverifiedOnly, search = "", status = selectedStatus, officer = selectedOfficer, branch = selectedBranch) => {
      setLoading(true);
      try {
        const url = `${API_BASE_URL}/api/v2/getModelData/personal_details`;
        const params = {
          limit: search ? 500 : limit,
          offset: search ? 0 : (page - 1) * limit,
          // A branch admin stays pinned to their own branch. A superadmin sends
          // undefined — every branch — unless they pick one from the filter.
          //
          // Top-level parameter, deliberately NOT part of `filter` below: the
          // server reads branchId from the query string, and it is already
          // scoping this endpoint for non-superadmins.
          branchId:
            roleName === "superadmin" ? (branch || undefined) : data.branchId,
          includeDepth: 1,
          relationshipType: "childToParent",
          fields: "id,customerUniqueId,fieldOfficerId,name,mobileNumber,adminverification,branchId",
        };

        const filterObj = {};
        if (status) {
          filterObj.adminverification = status;
        } else if (unverified) {
          filterObj.adminverification = "pending";
        }

        if (officer) {
          filterObj.fieldOfficerId = Number(officer);
        }

        if (Object.keys(filterObj).length > 0) params.filter = JSON.stringify(filterObj);
        if (search) params.searchTerm = search;

        const response = await axios.get(url, { params });
        const { data: responseData, count } = response.data;

        const formattedData = (responseData || []).map((customer) => {
          const staffRel = customer.fk_personal_details_belongsTo_staff_details_fieldOfficerId;
          return {
            ...customer,
            fieldOfficerName: staffRel ? staffRel.name : "N/A",
            customerUniqueId: customer.customerUniqueId || "No ID",
            name: customer.name || "Unknown",
            status: customer.adminverification || "Pending",
          };
        });

        setCustomerData(formattedData);
        setPagination({ page, limit, total: count || 0 });
      } catch (error) {
        console.error("Error fetching customers:", error);
        setAlertMessage("Error fetching data");
        setShowAlertModal(true);
      } finally {
        setLoading(false);
      }
    },
    [roleName, data.branchId, showUnverifiedOnly, selectedStatus, selectedOfficer, selectedBranch]
  );

  useEffect(() => {
    fetchData(pagination.page, pagination.limit, showUnverifiedOnly, debouncedSearch, selectedStatus, selectedOfficer, selectedBranch);
  }, [fetchData, pagination.page, pagination.limit, showUnverifiedOnly, debouncedSearch, selectedStatus, selectedOfficer, selectedBranch]);

  /* ── Handlers ─────────────────────────────────────── */
  const handleAddClick = () => navigate("/addEditCustomer", { state: { type: "Add" } });
  const handleEditClick = (id) => navigate("/addEditCustomer", { state: { type: "Edit", customerId: id } });
  const handleViewClick = (id) => navigate("/addEditCustomer", { state: { type: "View", customerId: id } });

  const handleDelClick = useCallback((id) => {
    setSelectedId(id);
    setShowDelModal(true);
  }, []);

  const handleVerifyClick = useCallback(async (id) => {
    // No guard on a missing customerUniqueId any more, and none is wanted: the
    // server MINTS one during verification (addDetails.js -> ensureCustomerUniqueId),
    // so refusing here would block a customer the server can now handle.
    //
    // The ID is not sent at all. It used to be, and "No ID" — the display
    // placeholder substituted below for customers without one — travelled as
    // though it were real, which is how a verify attempt came back with
    // "Field Officer ID not found": the server searched for a customer with that
    // literal value and found none.
    try {
      // ONE request. The server creates the savings account inside the same
      // transaction as the status change (addDetails.js), so either both land or
      // neither does.
      //
      // A second POST for model "account_details" used to follow this one. The
      // two were independent: the first committed `verified` and the second then
      // failed, leaving customers verified with no account — invisible to Member
      // Details, unable to take a loan, and with no way back because the Verify
      // button only renders for `pending` rows. 15 customers reached that state
      // on the live database.
      const res = await axios.post(`${API_BASE_URL}/api/addDetails`, {
        model: "personal_details",
        type: "Edit",
        id,
        adminverification: "verified",
      });

      if (res.data.message) {
        setAlertMessage("Customer verified successfully");
        fetchData(pagination.page, pagination.limit, showUnverifiedOnly, debouncedSearch);
      } else {
        setAlertMessage("Error: " + (res.data.error || "Failed to verify customer"));
      }
      setShowAlertModal(true);
    } catch (err) {
      console.error("Verification error:", err);
      setAlertMessage("An unexpected error occurred: " + (err.message || "Unknown error"));
      setShowAlertModal(true);
    }
  }, [fetchData, pagination, showUnverifiedOnly, debouncedSearch]);

  const handleCloseModal = useCallback(() => {
    setShowDelModal(false);
    setShowAlertModal(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    setIsDeleting(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/delDetails`,
        { model: "personal_details", id: selectedId },
        { headers: { "x-token": jwt } }
      );
      if (res.data.message) {
        setAlertMessage("Customer deleted successfully");
        fetchData(pagination.page, pagination.limit, showUnverifiedOnly, debouncedSearch);
      } else {
        setAlertMessage("Error: " + res.data.error);
      }
    } catch {
      setAlertMessage("An unexpected error occurred.");
    } finally {
      setIsDeleting(false);
      setShowDelModal(false);
      setShowAlertModal(true);
    }
  }, [selectedId, fetchData, pagination, showUnverifiedOnly, debouncedSearch, jwt]);

  const handlePageChange = (newPage) => fetchData(newPage, pagination.limit, showUnverifiedOnly, debouncedSearch);
  const handleLimitChange = (newLimit) => fetchData(1, newLimit, showUnverifiedOnly, debouncedSearch);

  /* ── Custom action (Verify button) ───────────────── */
  const renderCustomAction = (id) => {
    if (!isAdminRole) return null;
    const row = customerData.find((c) => c.id === id);
    if (!row || String(row.status).toLowerCase() !== "pending") return null;
    return (
      <Button
        variant="success"
        onClick={() => handleVerifyClick(row.id)}
        className="!px-2 !py-0.5 !text-[10px] h-6 font-semibold shadow-sm"
      >
        Verify
      </Button>
    );
  };

  /* ── Render ───────────────────────────────────────── */
  return (
    <PageLayout
      toolbar={
        <>
          {/* Search Bar & Total Badge */}
          <div className="flex items-center gap-2.5">
            <div className="w-48">
              <SearchInput
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            {pagination.total > 0 && (
              <Badge variant="primary" size="sm">
                {pagination.total.toLocaleString()} total
              </Badge>
            )}
          </div>

          {/* Filters & Actions */}
          <div className="flex flex-row flex-wrap items-center gap-2">
            {isAdminRole && (
              <Select
                value={showUnverifiedOnly ? "unverified" : "all"}
                onChange={(e) => {
                  const val = e.target.value;
                  setShowUnverifiedOnly(val === "unverified");
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                options={[
                  { value: "all", label: "All Customers" },
                  { value: "unverified", label: "Unverified Only" },
                ]}
                className="w-36 h-9 text-xs font-semibold"
              />
            )}

            <Select
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              options={[
                { value: "", label: "All Status" },
                { value: "verified", label: "Active" },
                { value: "pending", label: "Pending" },
                { value: "rejected", label: "Rejected" },
              ]}
              className="w-32 h-9 text-xs font-semibold"
            />

            <Select
              value={selectedOfficer}
              onChange={(e) => {
                setSelectedOfficer(e.target.value);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              options={[
                { value: "", label: "All Field Officers" },
                ...fieldOfficers.map((officer) => ({ value: officer.id, label: officer.name })),
              ]}
              className="w-44 h-9 text-xs font-semibold"
            />

            {/* Superadmin only. Every other role is already scoped to one branch
                by the request itself, so the filter would have nothing to do. */}
            {roleName === "superadmin" && (
              <Select
                value={selectedBranch}
                onChange={(e) => {
                  setSelectedBranch(e.target.value);
                  // Back to page 1: filtering to a smaller branch while on a
                  // later page would otherwise land on an empty list.
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                options={[
                  { value: "", label: "All Branches" },
                  ...branches.map((b) => ({ value: b.id, label: b.branchName })),
                ]}
                className="w-40 h-9 text-xs font-semibold"
              />
            )}

            {/* Add Customer (Data Entry Operator only if permitted) */}
            {hasAddPermission && roleName === "Data Entry Operator" && (
              <Button
                variant="primary"
                size="md"
                leftIcon={<PlusIcon />}
                onClick={handleAddClick}
              >
                Add Customer
              </Button>
            )}
          </div>
        </>
      }
    >
      {/* Table / Skeleton / Empty */}
      {loading ? (
        <TableSkeleton rows={10} cols={6} />
      ) : customerData.length === 0 ? (
        <div>
          <EmptyState
            variant="search"
            title="No customers found"
            description={searchTerm ? "Try adjusting your search terms or filters" : "No customer records match the current filters"}
            size="sm"
          />
        </div>
      ) : (
        <ServerTablePagination
          key={showUnverifiedOnly ? "unverified" : "all"}
          tabledata={customerData}
          totalCount={pagination.total}
          page={pagination.page}
          rowsPerPage={pagination.limit}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleLimitChange}
          showViewOption={true}
          /* showEditOption={hasEditPermission} */
          showDeleteOption={hasDelPermission}
          onClickView={handleViewClick}
          /* onClickEdit={handleEditClick} */
          onClickDel={handleDelClick}
          customAction={renderCustomAction}
          visibleFields={["name", "customerUniqueId", "mobileNumber", "fieldOfficerName", "status"]}
          customHeaders={{
            name: "Name",
            customerUniqueId: "Customer ID",
            mobileNumber: "Mobile",
            fieldOfficerName: "Field Officer",
            status: "Status",
          }}
        />
      )}
      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDelModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        title="Delete Customer"
        message="Are you sure you want to delete this customer? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        isLoading={isDeleting}
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={showAlertModal}
        onClose={handleCloseModal}
        title="Alert"
        message={alertMessage}
      />
    </PageLayout>
  );
};

export default Customers;